/** Validation keys map to dimension rules (frontend-only, checked before upload). */
export const IMAGE_VALIDATION_KEYS = {
  DYNAMIC_CONTENT_BANNER: 'dynamic_content_banner',
};

const RULES_BY_KEY = {
  [IMAGE_VALIDATION_KEYS.DYNAMIC_CONTENT_BANNER]: {
    minHeight: 210,
    maxHeight: 230,
    minWidth: 1050,
    maxWidth: 1600,
  },
};

/**
 * Read image pixel dimensions from a File (before upload).
 * @param {File} file
 * @returns {Promise<{ width: number, height: number }>}
 */
export function readImageDimensionsFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      reject(new Error('Invalid file'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image. Please use a valid JPG or PNG file.'));
    };
    img.src = url;
  });
}

/**
 * @param {number} width
 * @param {number} height
 * @param {{ minHeight?: number, maxHeight?: number, minWidth: number, maxWidth: number }} rules
 * @returns {{ valid: boolean, message: string }}
 */
export function validateDimensionsAgainstRules(width, height, rules) {
  if (!rules) {
    return { valid: true, message: '' };
  }
  if (
    rules.minHeight != null &&
    rules.maxHeight != null &&
    (height < rules.minHeight || height > rules.maxHeight)
  ) {
    return {
      valid: false,
      message: `Image height must be between ${rules.minHeight}px and ${rules.maxHeight}px (selected image is ${height}px).`,
    };
  }
  if (width < rules.minWidth || width > rules.maxWidth) {
    return {
      valid: false,
      message: `Image width must be between ${rules.minWidth}px and ${rules.maxWidth}px (selected image is ${width}px).`,
    };
  }
  return { valid: true, message: '' };
}

/**
 * Validate image dimensions on the client before upload (rules selected by validationKey).
 * @param {File} file
 * @param {string} validationKey
 * @returns {Promise<void>}
 */
export async function validateImageBeforeUpload(file, validationKey) {
  if (!validationKey) return;

  const rules = RULES_BY_KEY[validationKey];
  if (!rules) return;

  const { width, height } = await readImageDimensionsFromFile(file);
  const result = validateDimensionsAgainstRules(width, height, rules);
  if (!result.valid) {
    throw new Error(result.message);
  }
}

export function getImageValidationRules(validationKey) {
  return RULES_BY_KEY[validationKey] || null;
}
